/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/no-unescaped-entities */
import { useRouter } from "next/router";
import { Container, Row, Col } from "react-grid-system";
import theme from "@config/theme";
import AcceleratorLink from "@components/accelerators/AcceleratorLink";
import ProjectList from "@components/accelerators/ProjectList";
import { TrackedLink } from "@components/analytics/TrackedLink";
import Breadcrumbs from "@components/Breadcrumbs";
import Filter from "@components/Filter";
import { OptimizedImage } from "@components/OptimizedImage";
import Tags from "@components/Tags";
import { stringToURI } from "@scripts/utils";
import {
  ACCELERATOR_CATEGORIES,
  getAcceleratorsByFilters,
  getAcceleratorFeatures,
  getFeaturedAccelerators,
} from "./AcceleratorsHelper";

interface AcceleratorsPageProps {
  category: string;
  feature: string;
}
const AcceleratorsPage = ({ category, feature }: AcceleratorsPageProps) => {
  const router = useRouter();
  const featuredAccelerators = getFeaturedAccelerators();
  const acceleratorsByCategory = getAcceleratorsByFilters(category, feature);
  const acceleratorFeatures = getAcceleratorFeatures(category);

  const updateFilters = ({
    updatedCategory,
    updatedFeature,
  }: {
    updatedCategory: string;
    updatedFeature: string;
  }) => {
    router.push(
      {
        pathname: "/accelerators",
        query: {
          category: stringToURI(updatedCategory),
          feature: stringToURI(updatedFeature),
        },
      },
      undefined,
      { scroll: false },
    );
  };

  return (
    <>
      <Container>
        <Row>
          <Col md={12} lg={8}>
            <Breadcrumbs
              links={[{ name: "Accelerators", url: "/accelerators" }]}
            />
            <h1>Accelerators</h1>
            <p>
              Fully documented reference applications for assembling Notecard-
              and Notehub-powered solutions. Get your next project up and
              running fast.
            </p>
          </Col>
        </Row>

        <Row>
          <Col>
            <form className="filter-container">
              <label htmlFor="category-filter">Filter by:</label>

              <Filter
                id="category-filter"
                value={category}
                options={ACCELERATOR_CATEGORIES.map((c) => ({
                  key: stringToURI(c),
                  value: c,
                }))}
                placeholder="Categories"
                onChange={(value: string) =>
                  updateFilters({
                    updatedCategory: value,
                    updatedFeature: feature,
                  })
                }
              />

              <Filter
                value={feature}
                options={acceleratorFeatures}
                placeholder="Features"
                onChange={(value: string) =>
                  updateFilters({
                    updatedCategory: category,
                    updatedFeature: value,
                  })
                }
              />

              {(category || feature) && (
                <button
                  type="button"
                  className="button-as-link"
                  onClick={() =>
                    updateFilters({
                      updatedCategory: "",
                      updatedFeature: "",
                    })
                  }
                >
                  Clear Filters
                </button>
              )}
            </form>
          </Col>
        </Row>

        {!category &&
          !feature &&
          featuredAccelerators &&
          featuredAccelerators.length > 0 && (
            <Row>
              <Col>
                <section
                  className="section-wrapper"
                  data-testid="featured-projects"
                >
                  <h2>Featured</h2>
                  <div className="featured-card-list">
                    {featuredAccelerators.map((project) => (
                      <AcceleratorLink
                        key={project.id}
                        projectName={project.name}
                      >
                        <div key={project.id} className="featured-card">
                          <OptimizedImage
                            src={`/images/accelerators/app-banners/${project.id}-banner.png`}
                            alt=""
                          />
                          <div className="featured-card-content">
                            <h3>{project.name}</h3>
                            <p>{project.shortDescription}</p>
                            <Tags
                              tags={project.tags.map((tag: string) => ({
                                name: tag,
                              }))}
                            />
                          </div>
                        </div>
                      </AcceleratorLink>
                    ))}
                  </div>
                </section>
              </Col>
            </Row>
          )}

        <Row>
          <Col>
            {acceleratorsByCategory.map((p) => (
              <section key={p.heading} className="section-wrapper">
                <h2>{p.heading}</h2>
                <ProjectList projects={p.projects} />
              </section>
            ))}

            {acceleratorsByCategory.length === 0 && (
              <section className="section-wrapper">
                <p>No projects found with your selected filters.</p>
              </section>
            )}
          </Col>
        </Row>

        <Row>
          <Col>
            <p className="form-link">
              Have an app idea that's not on this list?{" "}
              <TrackedLink href="https://blues.com/landing-pages/accelerators-solutions-idea/">
                Let us know
              </TrackedLink>
            </p>
          </Col>
        </Row>

        <style jsx>
          {`
            h2 {
              margin-bottom: 3rem;
            }

            .section-wrapper {
              padding-bottom: 3rem;
              border-bottom: 1px solid ${theme.colors.gray4};
            }
            .section-wrapper {
              margin-top: 3rem;
            }

            :global(select) {
              width: 300px;
              text-overflow: ellipsis;
            }

            .form-link {
              padding-top: 1.5rem;
            }

            @media (max-width: ${theme.breakpoints.md}px) {
              .filter-container {
                flex-direction: column;
                align-items: flex-start;
              }
              .filter-container :global(select) {
                width: 100%;
              }
            }
          `}
        </style>
      </Container>
    </>
  );
};

export default AcceleratorsPage;
